import Resource, {BaseResource} from './Resource';
import { ContactSchema } from './Contact';
import {VehicleType} from "../../../../api-clients/ts";
import {BaseSignableDocuments} from "./SignableDocuments";

/**
 * Vehicle Definition Resource
 */
class Definition extends Resource
{
    constructor() {
        super('vehicle', ['vehicles']);
    }

    lookupByVrm(vrm, mileage, auditRef = null) {
        return this._doGet(['lookup'], {
            vrm, mileage, audit_ref: auditRef,
        });
    }

    lookupByVin(vin, mileage, auditRef = null) {
        return this._doGet(['lookup'], {
            vin, mileage, audit_ref: auditRef,
        });
    }

    refreshVehicleData(id) {
        return this._doPost([id, 'refresh'], {});
    }

    lookupSpecification(specification_id, specification_driver, vehicle_type, registration_date) {
        return this._doPost(['specifications', 'lookup'], { 
            specification_id,
            specification_driver,
            vehicle_type,
            registration_date
        });
    }

    refreshSpecification(id) {
        return this._doPost([id, 'specifications', 'refresh'], {});
    }
}

export let VehicleDefinitionResource =  new Definition();

/**
 * Vehicle Instance Resource
 */
class Instance extends Resource
{
    constructor() {
        super('vehicle.instance', ['stocklist']);
    }

    search(query, status = null, params = {}, config = {}) {
        params = Object.assign(params, { query });

        if (status) {
            params.status = status;
        }

        return this._doGet(['search'], params, config);
    }

    setChannels(id, data) {
        return this._doPost([id, 'advertising-channels'], data);
    }

    buy(id, data, params = {}) {
        return this._doPost([id, 'buy'], data, params);
    }

    warrantyOptionsFor(id) {
        return this._doGet([id, 'options', 'warranties'])
    }

    extendWarrantyFor(id, data) {
        return this._doPost([id, 'options', 'warranties'], data)
    }

    servicePlansFor(id) {
        return this._doGet([id, 'service-packs'])
    }

    tag(id, tag_name) {
        return this._doPost([id, 'tags'], { tag_name })
    }

    untag(id, tag_id) {
        return this._doDelete([id, 'tags', tag_id])
    }

    options() {
        return this._doGet(['options']);
    }
}

export let VehicleInstanceResource = new Instance();

/**
 * Vehicle Instance Resource
 */
class InstanceVideo extends Resource
{
    constructor() {
        super('vehicle.instance.video', ['stocklist', null, 'videos']);
    }

    forVehicle(id) {
        this.resourceSegments[1] = id;

        return this;
    }
}

export let VehicleInstanceVideoResource = new InstanceVideo();

/**
 * Vehicle Specification Resource
 */
class Specification extends Resource
{
    constructor() {
        super('vehicle.specification', ['vehicles', null, 'specifications']);
    }

    forVehicle(id) {
        this.resourceSegments[1] = id;

        return this;
    }

    allProviderSpecs(params) {
        return this._doGet(['provider'], params);
    }
}

export let VehicleSpecificationResource = new Specification();

/**
 * Vehicle Registrations
 */
class Registration extends Resource
{
    constructor() {
        super('vehicle.registration', ['vehicles', null, 'registrations']);
    }

    forVehicle(id) {
        this.resourceSegments[1] = id;

        return this;
    }
}

export let VehicleRegistrationResource = new Registration();

/**
 * Vehicle Mileages
 */
class MileageLog extends Resource
{
    constructor() {
        super('vehicle.mileage.entry', ['vehicles', null, 'log-book']);
    }

    estimate() {
        return this._doGet(['estimate']);
    }

    forVehicle(id) {
        this.resourceSegments[1] = id;

        return this;
    }
}

export let VehicleMileageLogResource = new MileageLog();

/**
 * Vehicle Range Resource
 * Manufactures, Ranges, Models & Derivatives
 */
class Range extends Resource
{
    constructor() {
        super(null, ['vehicles']);
    }

    manufacturers(params = {}) {
        return this._doGet(['manufacturers'], params);
    }

    ranges(manufacturer_id, params = {}) {
        return this._doGet(['manufacturers', manufacturer_id, 'ranges'], params);
    }

    models(range_id, params = {}) {
        return this._doGet(['ranges', range_id, 'models'], params);
    }

    derivatives(model_id, params = {}) {
        return this._doGet(['models', model_id, 'derivatives'], params);
    }

    async technicalData(derivative_id, params = {}) {
        return this._doGet(['derivatives', derivative_id, 'technical-data'])
    }

    resolveManufacturer(name) {
        return this._doGet(['manufacturers', 'resolve'], { name });
    }

    resolveModel(manufacturer_id, name) {
        return this._doGet(['models', 'resolve'], { manufacturer_id, name });
    }
}

export let VehicleRangeResource = new Range();

/**
 * Vehicle Instance Groups Resources
 */
class VehicleInstanceGroup extends Resource
{
    constructor() {
        super('vehicles.instance.group', ['stocklist', 'groups']);
    }

    find(id, params = {}, config = {}) {
        throw new Error('Not implemented');
    }

    addInstance(group_id, instance_id) {
        return this._doPost([group_id, instance_id])
    }

    removeInstance(group_id, instance_id) {
        return this._doDelete([group_id, instance_id])
    }
}

export let VehicleInstanceGroupResource = new VehicleInstanceGroup();

class VehicleInstanceFinanceQuote extends BaseResource
{
    constructor() {
        super();
        this.handle = 'vehicle.instance.finance.quote'
        this.resourceSegments = ['stocklist']
    }

    forInstance(id) {
        this.instance_id = id

        return this;
    }

    all(params = {}) {
        return this._doGet([this.instance_id, 'finance-quotes'], params)
    }

    defaultExamples(params = {}) {
        return this._doGet([this.instance_id, 'finance-quotes', 'default-examples'], params)
    }

    store(data = {}, params = {}, config = {}) {
        return this._doPost([this.instance_id, 'finance-quotes'], data, params, config);
    }

    destroy(id) {
        return this._doDelete([this.instance_id, 'finance-quotes', id])
    }

    lookup(quote_source, price, term, deposit, annual_mileage, credit_rating, quote_type, total_value_added_products = 0, apr) {
        return this._doPost([this.instance_id, 'finance-quotes', 'lookup'], {
            quote_source, price, term, deposit, annual_mileage, credit_rating, quote_type, total_value_added_products, apr
        })
    }
}

export let VehicleInstanceFinanceQuoteResource = new VehicleInstanceFinanceQuote();

export class SignableVehiclePurchaseInvoicePack extends BaseSignableDocuments
{
    constructor() {
        super('stocklist');
    }

    forStockListing(id) {
        return this.forModel(id)
    }
}

export let SignableVehiclePurchaseInvoicePackResource = new SignableVehiclePurchaseInvoicePack()

export let VehicleInstanceFinanceQuoteSchema = function(defaults = {}) {
    let schema = {
        price: '',
        term: '',
        deposit: '',
        annual_mileage: '',
        credit_rating: '',
        quote_type: '',
        product_name: '',
        product_type: '',
        total_credit: '',
        fixed_rate: '',
        representative_apr: '',
        first_payment: '',
        monthly_payment: '',
        final_payment: '',
        total_amount_payable: '',
        deal_id: '',
        error_message: '',
        lender_logo_url: '',
        total_value_added_products: '',
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}


/**
 * [VehicleIdentitySchema description]
 * @type {Object}
 */
export let VehicleIdentitySchema = function(defaults = {}) {
    let schema = {
    "response_type": "",
    "vehicle_type": "",
    "type": "",
    "specification_id": "",
    "specification_driver": "",
    "audit_ref": "",
    "registration": "",
    "vin": "",
    "engine_number": "",
    "manufacturer": {
    "id": "",
    "name": "",
    "created_at": "",
    "updated_at": "",
    },
    "model": {
    "id": "",
    "name": "",
    "manufacturer_id": "",
    "created_at": "",
    "updated_at": ""
    },
    "derivative": "",
    "body_type": "",
    "fuel_type": "",
    "transmission_type": "",
    "engine_size": "",
    "co2": "",
    "colour": "",
    "number_of_doors": "",
    "number_of_seats": "",
    "registration_date": "",
    "year_of_manufacture": "",
    "plate": "",
    "is_imported": "",
    "timestamp": "",

    "list_price": "",
    "insurance_group": "",
    "mpg_combined": "",
    "manufacturer_warranty_years": "",
    "manufacturer_warranty_miles": "",
    "tyre_size_front": "",
    "tyre_size_rear": "",
    "is_new_or_unregistered": false,
    "has_temporary_vin": false,
    "bedroom_layout": "",
    "end_layout": "",
    "berths": "",
    "bedrooms": "",
    "drive_train": "",
    "drive_position": "",
    "interior_trim": "",
    "interior_colour": "",
    };

    return Object.assign(schema, defaults);
};

/**
 * [VehicleDefinitionSchema description]
 * @type {Object}
 */
export let VehicleDefinitionSchema = function(defaults = null) {
    let schema = {
        "specification_id": "",
        "specification_driver": "",
        "vehicle_type": "",
        "type": "",
        "vin": "",
        "manufacturer_id": "",
        "model_id": "",
        "file_as": "",
        "derivative": "",
        "body_type": "",
        "fuel_type": "",
        "emission_class": "",
        "transmission_type": "",
        "engine_size": "",
        "co2": "",
        "colour": "",
        "number_of_doors": "",
        "number_of_seats": "",
        "registration_date": "",
        "id": "",
        "created_by_id": "",
        "created_at": "",
        "updated_at": "",
        "mileage": "",
        "battery_charge_time": "",
        "battery_quick_charge_time": "",
        "battery_range_miles": "",
        "battery_capacity_kwh": "",
        "battery_usable_capacity_kwh": "",

        "art_end_date": "",
        "is_taxed": "",
        "tax_due_date": "",
        "last_v5_issue_date": "",
        "mot_expiry_date": "",
        "list_price": "",
        "insurance_group": "",
        "mpg_combined": "",
        "manufacturer_warranty_years": "",
        "manufacturer_warranty_miles": "",
        "tyre_size_front": "",
        "tyre_size_rear": "",
        "bedroom_layout": "",
        "end_layout": "",
        "berths": "",
        "bedrooms": "",
        registration: new VehicleRegistrationSchema(),
    };

    return Object.assign(schema, defaults);
};

export let VehicleRegistrationSchema = function(defaults = {}) {
    let schema = {
        vrm: null,
        vehicle_id: null
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}

export let VehicleMileageLogSchema = function(defaults = {}) {
    let schema = {
        miles: null,
        notes: null,
        vehicle_id: null,
        booking_id: null,
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
}

/**
 * [VehicleInstanceSchema description]
 * @type {Object}
 */
export let VehicleInstanceSchema = function(defaults = {}) {
    let schema = {
        "vehicle_id": "",
        "vehicle": VehicleDefinitionSchema,
        "location_id": "",
        "owner_id": "",
        "owner": ContactSchema,
        "owners": "",
        "retail_price": "",
        "is_vat_qualifier": "",
        "vat_reclaimed_on": null,
        "is_part_exchange": "",
        "delivery_on": "",
        "for_sale_on": "",
        "disposed_on": "",
        "handed_over_on": "",
        "id": "",
        "created_by_id": "",
        "created_at": "",
        "updated_at": "",
        "life_cycle_status": "",
        "title": "",
        "description": "",
        "life_cycle_status_text": "",
        "key_number": "",
        "margin": "",
        "stand_in_value": "",
        "market": "",
        "forecourt_phase": "None",
        "advertising_comments": null,
        "advertising_attention_grabber": null,
        "service_history": null,
        "v5_status": null,
        "v5_document_reference": null,
        "advertise": false,
        "is_sale_or_return": false,
        "siv_base": null,
        "siv_adjustment": null,
        "vat_treatment": "auto"
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
};

export let VehicleSpecificationSchema = function(defaults = {}) {
    let schema = {
        provider_key: null,
        provider_specification_id: null,
        provider_specification_item_i: null,
        specification_item_group_id: 'Other',
        label: null,
        generic_label: null,
        description: null,
        price: null,
        is_pack: false,
        is_option: true,
        is_default: false,
        id: null,
        created_at: null,
        updated_at: null,
    };

    if (defaults) {
        schema = Object.assign(schema, defaults);
    }

   return schema;
};

/**
 * [VehicleServiceHistoryTypes description]
 * @type {Array}
 */
export const VehicleServiceHistoryList = [
    { value: 'Partial', label: 'Partial' },
    { value: 'Full', label: 'Full' },
    { value: 'Full - Main Dealer', label: 'Full - Main Dealer' },
];

export const VehicleV5StatusList = [
    { value: 'Yes', label: 'Yes' },
    { value: 'PTT', label: 'PTT' },
    { value: 'Motability', label: 'Motability' },
];

/**
 * [VatQualificationTypes description]
 * @type {Array}
 */
export const VatQualificationTypes = [
    { value: 0, label: 'Non-Qualifying' },
    { value: 1, label: 'Qualifying' },
];

/**
 * [VehicleLookupDescription description]
 * @type {Object}
 */
export let VehicleLookupDescription = function() {
    return {
        vin: '',
        registration: 'YD04XKX',
        mileage: '140000',
    };
};

export const VehicleLifeCycleStatuses = {
    NON_STOCK: 100,
    DUE_IN: 5,
    IN_PREP: 7,
    IN_STOCK: 10,
    RESERVED: 15,
    AWAITING_DELIVERY: 20,
    SOLD: 25,
    WRITTEN_OFF: 90,
};

export const VehicleInstanceMarkets = {
    NONE: 'none',
    TRADE: 'trade',
    RETAIL: 'retail',
};

export const VehicleHistoricPriceIndicators = {
    UP: 'up',
    DOWN: 'down',
    STATIC: 'static'
}

export let VehicleInstanceVideoTypes = {
    TYPE_YOUTUBE: "youtube",
};

export const getVehicleTypeBodyList = (type) => {
    const typedLists = {}

    typedLists[VehicleType.Car] = [
        'Camper',
        'Car Derived Van',
        'Combi Van',
        'Convertible',
        'Coupe',
        'Estate',
        'Hatchback',
        'Limousine',
        'MPV',
        'Minibus',
        'Panel Van',
        'Pickup',
        'SUV',
        'Saloon',
        'Unlisted',
        'Window Van',
    ]

    typedLists[VehicleType.Lcv] = [
        'Box Van',
        'Camper',
        'Car Derived Van',
        'Chassis Cab',
        'Combi Van',
        'Curtain Side',
        'Dropside',
        'High Roof Van',
        'Luton',
        'MPV',
        'Minibus',
        'Panel Van',
        'Pickup',
        'SUV',
        'Specialist Vehicle',
        'Temperature Controlled',
        'Tipper',
        'Vehicle Transporter',
        'Welfare',
        'Window Van',
        'Unlisted',
    ]

    typedLists[VehicleType.Motorcycle] = [
        'Adventure',
        'Classic',
        'Commuter',
        'Custom Cruiser',
        'E-Bike',
        'Enduro',
        'Minibike',
        'Moped',
        'Motocrosser',
        'Naked',
        'Quad/ATV',
        'Roadster/Retro',
        'Scooter',
        'Sidecar',
        'Special',
        'Sports Tourer',
        'Super Moto',
        'Super Sports',
        'Supermoto-Road',
        'Three Wheeler',
        'Tourer',
        'Trail',
        'Trail Bike',
        'Trial Bike',
        'Trials Bike',
        'Unlisted',
    ]

    typedLists[VehicleType.Hgv] = [
        'Beavertail',
        'Box',
        'Bus',
        'Catering',
        'Chassis Cab',
        'Coach',
        'Concrete Mixer',
        'Crane Mounted',
        'Curtain Side',
        'Dropside',
        'Flatbed',
        'Gritter Truck',
        'Hook Loader',
        'Livestock',
        'Low Loader',
        'Municipal',
        'Pantechnicon',
        'Road Sweeper',
        'Scaffold Body',
        'Skeletal',
        'Skip Loader',
        'Specialist Vehicle',
        'Tanker',
        'Temperature Controlled',
        'Tipper',
        'Tipper Grab',
        'Tractor Unit',
        'Unlisted',
        'Vehicle Transporter',
    ]

    typedLists[VehicleType.Motorhome] = [
        'A Class',
        'American RV',
        'C Class',
        'Campervan',
        'Coach Built',
        'High Top',
        'Low Profile',
        'Micro Motorhome',
        'Motorhome',
        'Van Conversion',
    ]

    typedLists[VehicleType.Other] = [
        'Unlisted'
    ]

    if (typedLists[type]) {
        return typedLists[type]
    }

    return typedLists[VehicleType.Other]
}

export let VehicleColourList = [
    "White",
    "Black",
    "Silver",
    "Grey",
    "Beige",
    "Red",
    "Blue",
    "Orange",
    "Yellow",
    "Gold",
    "Bronze",
    "Green",
    "Brown",
    "Purple",
    "Magenta",
    "Burgundy",
    "Turquoise",
    "Maroon",
    "Pink",
];

export let VehicleDoorList = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
];

export let VehicleSeatsList = [
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
];

export let VehicleInsuranceGroupList = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
    '24',
    '25',
    '26',
    '27',
    '28',
    '29',
    '30',
    '31',
    '32',
    '33',
    '34',
    '35',
    '36',
    '37',
    '38',
    '39',
    '40',
    '41',
    '42',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '49',
    '50',
];

export let VehicleTransmissionList = [
    "Manual",
    "Automatic",
];

export let VehicleFuelTypeList = [
    "Bi Fuel",
    "Diesel",
    "Diesel Hybrid",
    "Diesel Plug-in Hybrid",
    "Electric",
    "Hydrogen",
    "Petrol",
    "Petrol Hybrid",
    "Petrol Plug-in Hybrid",
];